import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setUser, setAuthToken, setAuthType } from '../../store/slices/userSlice';
import { validateOTP, fetchUserData, requestOTP } from '../../api/auth';
import { setAuthToken as saveAuthToken, setAuthType as saveAuthType, setUserData as saveUserData } from '../../utils/localStorage';
import { PHONE_AUTH_TYPE } from '../../utils/constants';
import routes from '../../utils/routes';
import strings from '../../utils/strings';
import { CButton, CInput, CText, CHeader } from '../../components/common';

const OTPVerification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const colors = useAppSelector((state) => state.theme.theme);

  const phoneNumber = location.state?.phoneNumber || '';
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerifyOTP = async () => {
    if (!otp) {
      setOtpError(strings.thisFieldIsMandatory);
      return;
    }

    if (otp.length < 6) {
      setOtpError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      const response = await validateOTP(phoneNumber, otp);
      console.log('OTP validation response:', response);

      saveAuthToken(response.access || '');
      saveAuthType(PHONE_AUTH_TYPE);
      dispatch(setAuthToken(response.access || ''));
      dispatch(setAuthType(PHONE_AUTH_TYPE));

      const userData = await fetchUserData(response.access || '');
      dispatch(setUser(userData));
      saveUserData(userData);

      if (response.user_exists === false || !userData.user_exists) {
        navigate(routes.userDetails || '/user-details');
      } else {
        navigate(routes.home);
      }
    } catch (error: any) {
      console.error('Error validating OTP:', error);
      setOtpError(
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Invalid OTP. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      await requestOTP(phoneNumber);
      alert('OTP has been resent successfully');
      setOtp('');
      setOtpError('');
    } catch (error: any) {
      console.error('Error resending OTP:', error);
      alert(error.response?.data?.error || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <CHeader title="Verify OTP" />

      <div className="max-w-md mx-auto px-5 py-4">
        <h2
          className="text-2xl font-bold mb-2"
          style={{ color: colors.textColor }}
        >
          {strings.otp}
        </h2>
        <CText type="r14" color={colors.grayScale500} style={{ marginBottom: '16px' }}>
          Enter the 6-digit code sent to {phoneNumber}
        </CText>

        <CInput
          label={strings.otp}
          value={otp}
          onChange={setOtp}
          placeholder="Enter 6-digit OTP"
          type="text"
          maxLength={6}
          errorText={otpError}
          required
        />

        <div className="text-center mt-4 mb-4">
          <CText type="r14" color={colors.grayScale500}>
            Didn't receive the code?{' '}
            <span
              className="cursor-pointer font-semibold"
              style={{ color: colors.primary }}
              onClick={handleResendOTP}
            >
              {strings.resendOTP}
            </span>
          </CText>
        </div>

        <CButton
          title={strings.verifyOTP}
          onPress={handleVerifyOTP}
          disabled={loading || otp.length < 6}
        />

        <div className="text-center mt-4">
          <span
            className="text-sm cursor-pointer"
            style={{ color: colors.primary }}
            onClick={() => navigate(routes.login)}
          >
            Back to Login
          </span>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
